import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Printer, FileSpreadsheet, X, Save, Loader2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { qualityPPMAPI } from '@/lib/api';
import { KpiCards, CustomerTiles, TrendChart, ReasonList, BreakdownTable, DetailTable } from './ppm/PPMWidgets';
import { MonthlyEmailCard, LinkProgramDialog } from './ppm/PPMAdmin';
import { ParetoCard } from './ppm/ParetoCard';

const iso = (d) => d.toISOString().slice(0, 10);
const defaultFrom = () => { const d = new Date(); d.setMonth(d.getMonth() - 11, 1); return iso(d); };
const monthRange = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return [`${m}-01`, iso(new Date(Date.UTC(y, mo, 0)))];
};

const TargetEditor = ({ targets, onSaved }) => {
  const [rej, setRej] = useState(targets.target_rejection_pct);
  const [qua, setQua] = useState(targets.target_quarantine_pct);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRej(targets.target_rejection_pct); setQua(targets.target_quarantine_pct); }, [targets]);
  const save = async () => {
    setSaving(true);
    try {
      const res = await qualityPPMAPI.setTargets({ target_rejection_pct: parseFloat(rej), target_quarantine_pct: parseFloat(qua) });
      toast.success('KPI targets saved'); onSaved(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save targets'); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-end gap-2 border-l pl-4" data-testid="ppm-target-editor">
      <div><Label className="text-xs">Target rejection %</Label>
        <Input type="number" step="0.01" min="0" value={rej} onChange={(e) => setRej(e.target.value)} className="h-9 w-24 bg-amber-50" data-testid="ppm-target-rejection-input" /></div>
      <div><Label className="text-xs">Target quarantine %</Label>
        <Input type="number" step="0.01" min="0" value={qua} onChange={(e) => setQua(e.target.value)} className="h-9 w-24 bg-amber-50" data-testid="ppm-target-quarantine-input" /></div>
      <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="ppm-target-save-btn">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save
      </Button>
    </div>
  );
};

const Chip = ({ label, onClear, testid }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs px-2.5 py-1" data-testid={testid}>
    {label}<button onClick={onClear} className="hover:text-red-600" data-testid={`${testid}-clear`}><X className="w-3 h-3" /></button>
  </span>
);

const exportCsv = (rows) => {
  const cols = ['date', 'ncr_number', 'wo_number', 'item_unique_id', 'item_name', 'program_name', 'customer', 'quarantined', 'disposition', 'rejected', 'reworked', 'reaccepted', 'pending', 'reason', 'status', 'by'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `rejection_ppm_${iso(new Date())}.csv`; a.click();
};

export default function RejectionPPMPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(iso(new Date()));
  const [range, setRange] = useState({ from: defaultFrom(), to: iso(new Date()) });
  const [month, setMonth] = useState(null);
  const [drill, setDrill] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linkItem, setLinkItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [df, dt] = month ? monthRange(month) : [range.from, range.to];
    try {
      const res = await qualityPPMAPI.report({ date_from: df, date_to: dt, program_id: drill.program?.id, item_id: drill.item?.id, customer: drill.customer });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [range, month, drill]);

  useEffect(() => { load(); }, [load]);

  const setDrillKey = (k, v) => setDrill((d) => ({ ...d, [k]: v }));
  const anyDrill = month || drill.program || drill.item || drill.customer;

  return (
    <div className="space-y-4" data-testid="rejection-ppm-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">Rejection / Quarantine PPM</h1>
        <p className="text-xs text-muted-foreground">Production quarantine (MRB) against total produced (OK + quarantined). Rejected = MRB qty dispositioned as reject.</p>
      </div>

      <Card className="shadow-none print:hidden"><CardContent className="p-3 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="ppm-from-date" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="ppm-to-date" /></div>
        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-9" onClick={() => { setMonth(null); setRange({ from, to }); }} data-testid="ppm-show-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}Show
        </Button>
        {isAdmin && data && <TargetEditor targets={data.targets} onSaved={(t) => setData((d) => ({ ...d, targets: t }))} />}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="ppm-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Print / PDF</Button>
          <Button size="sm" variant="outline" onClick={() => data && exportCsv(data.details)} data-testid="ppm-excel-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
        </div>
      </CardContent></Card>

      {anyDrill && (
        <div className="flex flex-wrap items-center gap-2" data-testid="ppm-drill-chips">
          <span className="text-xs text-muted-foreground">Drill-down:</span>
          {month && <Chip label={`Month: ${month}`} onClear={() => setMonth(null)} testid="ppm-chip-month" />}
          {drill.program && <Chip label={`Program: ${drill.program.name}`} onClear={() => setDrillKey('program', null)} testid="ppm-chip-program" />}
          {drill.customer && <Chip label={`Customer: ${drill.customer}`} onClear={() => setDrillKey('customer', null)} testid="ppm-chip-customer" />}
          {drill.item && <Chip label={`Item: ${drill.item.name}`} onClear={() => setDrillKey('item', null)} testid="ppm-chip-item" />}
          <button className="text-xs text-red-600 hover:underline" onClick={() => { setMonth(null); setDrill({}); }} data-testid="ppm-clear-drill">Clear all</button>
        </div>
      )}

      {data && (
        <>
          <KpiCards totals={data.totals} targets={data.targets} />
          {data.customer && <CustomerTiles c={data.customer} />}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <TrendChart data={data.monthly} targets={data.targets} onMonthClick={setMonth} />
            <ReasonList rows={data.by_reason} />
          </div>
          <ParetoCard dateFrom={data.date_from} dateTo={data.date_to} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <BreakdownTable title="By program — click to drill down" rows={data.by_program} testid="ppm-by-program"
              keyOf={(r) => r.program_id} labelOf={(r) => r.program_name} targetPct={data.targets.target_rejection_pct}
              onPick={(r) => setDrillKey('program', { id: r.program_id, name: r.program_name })} />
            <BreakdownTable title="By customer — click to drill down" rows={data.by_customer} testid="ppm-by-customer"
              keyOf={(r) => r.customer} labelOf={(r) => r.customer} targetPct={data.targets.target_rejection_pct}
              onPick={(r) => setDrillKey('customer', r.customer)} />
          </div>
          <BreakdownTable title="By product / item — click to drill down" rows={data.by_item} testid="ppm-by-item"
            keyOf={(r) => r.item_id} targetPct={data.targets.target_rejection_pct}
            labelOf={(r) => <><span className="font-mono">{r.item_unique_id}</span> <span className="text-muted-foreground no-underline">{r.item_name} · {r.program_name}</span></>}
            onPick={(r) => setDrillKey('item', { id: r.item_id, name: r.item_unique_id || r.item_name })}
            rowAction={(r) => r.program_name === 'Unassigned' && (
              <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => setLinkItem(r)} data-testid={`ppm-link-program-btn-${r.item_id}`}>
                <Link2 className="w-3 h-3 mr-1" />Link program</Button>)} />
          <DetailTable rows={data.details} onOpenCase={(id) => navigate(`/quality/mrb?case=${id}`)} />
        </>
      )}
      {isAdmin && <MonthlyEmailCard />}
      <LinkProgramDialog item={linkItem} onClose={() => setLinkItem(null)} onLinked={() => { setLinkItem(null); load(); }} />
    </div>
  );
}
